import { useState, React } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { styled } from "@mui/material/styles";
import Notes from './header';
import FooterManual from "./footerManual";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import BankIcon from "@mui/icons-material/AccountBalance";
import CardIcon from "@mui/icons-material/CreditCard";
import InputAdornment from "@mui/material/InputAdornment";
import PaymentIcon from "@mui/icons-material/Payment";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LockIcon from "@mui/icons-material/Lock";
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import CopyAllIcon from '@mui/icons-material/CopyAll';
import IconButton from '@mui/material/IconButton';
import { useLocation, useNavigate } from "react-router-dom";
import Snackbar from '@mui/material/Snackbar';

const PaymentInformation = () => {
  const [paymentMethod, setPaymentMethod] = useState("bank");
  const [selectedBankIndex, setSelectedBankIndex] = useState(0);
  const [bankTermsAccepted, setBankTermsAccepted] = useState(false);
  const [cardTermsAccepted, setCardTermsAccepted] = useState(false);
  const [receiptUploaded, setReceiptUploaded] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState("pending");
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardDetails, setCardDetails] = useState({
    number: "",
    name: "",
    expiry: "",
    cvv: "",
  });
  const location = useLocation();
  const navigate = useNavigate();
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const { totalPayable, shippingDetails, selectedAddress, cars } = location.state || {};
  // Set default values if no state was passed
  const defaultTotal = "$1,250.00"; // Your fallback value
  const [paymentAmount, setPaymentAmount] = useState(totalPayable || defaultTotal);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Banks data remains the same...

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const processPayment = async () => {
    setIsProcessing(true);
    const token = localStorage.getItem("authToken");

    try {
      let paymentData;

      if (paymentMethod === "bank") {
        paymentData = {
          nameOnCard: "",
          cardNumber: 0,
          expiryMonth: 0,
          expiryYear: 0,
          cvv: 0,
          isBankTransfer: true
        };
      } else {
        const [expiryMonth, expiryYear] = cardDetails.expiry.split('/');

        paymentData = {
          nameOnCard: cardDetails.nameOnCard,
          cardNumber: parseInt(cardDetails.cardNumber.replace(/\s/g, '')),
          expiryMonth: parseInt(expiryMonth),
          expiryYear: parseInt(expiryYear),
          cvv: parseInt(cardDetails.cvv),
          isBankTransfer: false
        };
      }

      const response = await fetch('https://car.amar.co.jp/api/dashboard/checkout/processPayment', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(paymentData)
      });

      if (!response.ok) {
        throw new Error('Payment processing failed');
      }

      const result = await response.json();
      setPaymentStatus("completed");

      // Show success popup
      setShowSuccessPopup(true);

      // Hide popup after 2 seconds and redirect
      setTimeout(() => {
        setShowSuccessPopup(false);
        navigate('/order'); // Redirect to your order page
      }, 2000);

    } catch (error) {
      console.error('Payment error:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Payment failed. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBankTransferSubmit = () => {
    processPayment();
  };

  const handleCardPaymentSubmit = () => {
    processPayment();
  };

  const handleCardChange = (prop) => (event) => {
    // Format card number with spaces every 4 digits
    if (prop === 'cardNumber') {
      const value = event.target.value.replace(/\D/g, '');
      const formattedValue = value.replace(/(\d{4})(?=\d)/g, '$1 ');
      setCardDetails({ ...cardDetails, [prop]: formattedValue });
    }
    // Handle expiry date formatting
    else if (prop === 'expiry') {
      let value = event.target.value.replace(/\D/g, '');
      if (value.length > 2) {
        value = value.substring(0, 2) + '/' + value.substring(2, 4);
      }
      setCardDetails({ ...cardDetails, expiry: value });

      // Also update the separate month/year fields for the API
      const [month, year] = value.split('/');
      setCardDetails(prev => ({
        ...prev,
        expiryMonth: month || '',
        expiryYear: year || ''
      }));
    }
    // For other fields
    else {
      setCardDetails({ ...cardDetails, [prop]: event.target.value });
    }
  };

  const banks = [
    {
      name: "SUMITO MITSUBUSHI BANKING CORPORATION",
      accountName: "AMAR ENTERPRISE CO. LTD",
      accountNumber: "7160389",
      iban: "NA",
      swift: "SMBC-JPJT",
      currency: "USD",
      icon: <BankIcon color="primary" />,
      benefits: ["No transfer fees", "Instant processing", "24/7 support"],
      recommended: true
    },
    {
      name: "SUMITO MITSUBUSHI BANKING CORPORATION",
      accountName: "AMAR ENTERPRISE CO. LTD",
      accountNumber: "7160389",
      iban: "NA",
      swift: "SMBC-JPJT",
      currency: "USD",
      icon: <BankIcon color="secondary" />,
      benefits: ["Best for EU customers", "Low exchange rates", "Same-day transfers"]
    },
    {
      name: "SUMITO MITSUBUSHI BANKING CORPORATION",
      accountName: "AMAR ENTERPRISE CO. LTD",
      accountNumber: "7160389",
      iban: "NA",
      swift: "SMBC-JPJT",
      currency: "USD",
      icon: <BankIcon color="success" />,
      benefits: ["Optimized for Asia", "Multi-currency support", "Dedicated manager"]
    }
  ];

  const handlePaymentMethodChange = (event, newValue) => {
    setPaymentMethod(newValue);
  };

  const handleBankSelect = (event) => {
    setSelectedBankIndex(parseInt(event.target.value));
  };

  const handleBankTermsChange = (event) => {
    setBankTermsAccepted(event.target.checked);
  };

  const handleCardTermsChange = (event) => {
    setCardTermsAccepted(event.target.checked);
  };

  const handleReceiptUpload = (event) => {
    if (event.target.files && event.target.files[0]) {
      setReceiptUploaded(true);
      setPaymentStatus("under_review");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  });

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      border: "2px solid #EBEBEB",
      borderRadius: "8px",
      backgroundColor: "#fff",
      "& fieldset": {
        border: "none",
      },
      "&:hover": {
        border: "2px solid #Ef4444",
      },
      "&.Mui-focused": {
        border: "2px solid #Ef4444",
      },
    },
  };
  if (!location.state) {
    // You might want to redirect them back or show an error
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <Typography variant="h5" color="error" sx={{ mb: 2 }}>
          Missing Payment Information
        </Typography>
        <Typography sx={{ mb: 3 }}>
          Please complete the delivery information before proceeding to payment.
        </Typography>
        <Button
          variant="contained"
          color="error"
          onClick={() => window.location.href = '/delivery'} // Or use navigate if you have it
        >
          Go Back to Delivery Section
        </Button>
      </Box>
    );
  }

  return (
    <div>
      <Notes />
      {showSuccessPopup && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999
          }}
        >
          <Card
            sx={{
              padding: 4,
              maxWidth: 400,
              textAlign: 'center',
              backgroundColor: '#4caf50',
              color: 'white'
            }}
          >
            <Typography variant="h5" sx={{ mb: 2 }}>
              Order Placed Successfully!
            </Typography>
            <Typography>
              Please check your email for confirmation.
            </Typography>
          </Card>
        </Box>
      )}

      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          padding: "20px",
          backgroundColor: "#f8f9fa",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <Grid container justifyContent="center">
          <Grid item xs={12} md={8} lg={6}>
            <Card
              sx={{
                padding: { xs: "15px", md: "25px" },
                border: "2px solid #EBEBEB",
                borderRadius: "10px",
                backgroundColor: "#ffffff",
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: "bold", marginBottom: "20px", textAlign: "center" }}>
                Payment Information
              </Typography>

              <Tabs
                value={paymentMethod}
                onChange={handlePaymentMethodChange}
                variant="fullWidth"
                sx={{ mb: 3 }}
              >
                <Tab
                  value="bank"
                  label="Bank Transfer"
                  icon={<BankIcon />}
                  iconPosition="start"
                  sx={{ textTransform: 'none' }}
                />
                <Tab
                  value="card"
                  label="Credit/Debit Card"
                  icon={<CardIcon />}
                  iconPosition="start"
                  sx={{ textTransform: 'none' }}
                />
              </Tabs>

              {paymentMethod === "bank" ? (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', textAlign: 'center' }}>
                    Select Bank Account for Transfer
                  </Typography>

                  <RadioGroup
                    value={selectedBankIndex}
                    onChange={handleBankSelect}
                    sx={{ mb: 3 }}
                  >
                    {banks.map((bank, index) => (
                      <Card
                        key={index}
                        sx={{
                          p: 2,
                          mb: 2,
                          border: selectedBankIndex === index ? "2px solid #Ef4444" : "1px solid #EBEBEB",
                          backgroundColor: "#f9f9f9",
                          position: 'relative'
                        }}
                      >
                        {bank.recommended && (
                          <Box sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            backgroundColor: '#Ef4444',
                            color: 'white',
                            px: 1,
                            borderRadius: '0 0 0 4px',
                            fontSize: '0.75rem'
                          }}>
                            Recommended
                          </Box>
                        )}
                        <FormControlLabel
                          value={index}
                          control={<Radio sx={{ color: "#Ef4444", "&.Mui-checked": { color: "#Ef4444" } }} />}
                          label={
                            <Box sx={{ width: '100%' }}>
                              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                                {bank.icon}
                                <Box sx={{ ml: 1 }}>{bank.name}</Box>
                                <Tooltip title={`Best for ${bank.currency} transfers`}>
                                  <InfoIcon color="action" sx={{ ml: 1, fontSize: '1rem' }} />
                                </Tooltip>
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {bank.accountName} • {bank.currency}
                              </Typography>
                            </Box>
                          }
                          sx={{ width: '100%', m: 0 }}
                        />
                      </Card>
                    ))}
                  </RadioGroup>

                  <Card sx={{
                    p: 3,
                    mb: 3,
                    backgroundColor: "#f9f9f9",
                    border: "1px solid #EBEBEB",
                  }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', textAlign: 'center' }}>
                      {banks[selectedBankIndex].name} Transfer Details
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {[
                        { label: "Account Name", value: banks[selectedBankIndex].accountName },
                        { label: "Account Number", value: banks[selectedBankIndex].accountNumber },
                        { label: "IBAN", value: banks[selectedBankIndex].iban },
                        { label: "SWIFT/BIC", value: banks[selectedBankIndex].swift },
                        { label: "Currency", value: banks[selectedBankIndex].currency },
                      ].map((field, i) => (
                        <Grid item xs={12} md={6} key={i}>
                          <Box sx={{
                            p: 1.5,
                            backgroundColor: 'white',
                            borderRadius: '6px',
                            border: '1px solid #EBEBEB'
                          }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              {field.label}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {field.value}
                              </Typography>
                              <Tooltip title="Copy to clipboard">
                                <IconButton
                                  size="small"
                                  onClick={() => copyToClipboard(field.value)}
                                  sx={{ ml: 1, color: '#Ef4444' }}
                                >
                                  <CopyAllIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>

                    <Accordion sx={{ mb: 0 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Bank Benefits & Instructions</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          Please include your invoice number (INV-12345) in the transfer reference.
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          Benefits of using {banks[selectedBankIndex].name}:
                        </Typography>
                        <ul style={{ marginTop: 0, paddingLeft: 20 }}>
                          {banks[selectedBankIndex].benefits.map((benefit, i) => (
                            <li key={i}>{benefit}</li>
                          ))}
                        </ul>
                        <Typography variant="body2" color="text.secondary">
                          Transfers typically take 1-3 business days to process.
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  </Card>

                  <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={bankTermsAccepted}
                          onChange={handleBankTermsChange}
                          sx={{ color: "#Ef4444", "&.Mui-checked": { color: "#Ef4444" } }}
                        />
                      }
                      label="I confirm the payment details are correct and agree to the Terms and Conditions"
                    />
                  </Box>

                  <Card sx={{ p: 2, mb: 3, backgroundColor: "#f9f9f9", border: "1px solid #EBEBEB" }}>
                    <Typography variant="h6" sx={{ textAlign: "center" }}>Total Payment Amount</Typography>
                    <Typography variant="h4" sx={{ textAlign: "center", color: "#Ef4444", fontWeight: "bold" }}>
                      {paymentAmount}
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "center", mt: 1 }}>
                      {banks[selectedBankIndex].currency} equivalent will be calculated by your bank
                    </Typography>
                  </Card>

                  {paymentMethod === "bank" ? (
                    <Button
                      variant="contained"
                      fullWidth
                      disabled={!bankTermsAccepted || isProcessing}
                      onClick={handleBankTransferSubmit}
                      sx={{
                        mb: 3,
                        backgroundColor: "#Ef4444",
                        "&:hover": { backgroundColor: "#D32F2F" },
                        "&:disabled": { backgroundColor: "#cccccc" },
                        height: '48px',
                        fontSize: '1rem'
                      }}
                    >
                      {isProcessing ? 'Processing...' : 'ORDER NOW'}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      fullWidth
                      disabled={!cardTermsAccepted || isProcessing}
                      onClick={handleCardPaymentSubmit}
                      sx={{
                        backgroundColor: "#Ef4444",
                        "&:hover": { backgroundColor: "#D32F2F" },
                        "&:disabled": { backgroundColor: "#cccccc" },
                        height: '48px',
                        fontSize: '1rem'
                      }}
                    >
                      {isProcessing ? 'Processing...' : 'Pay Now Securely'}
                    </Button>
                  )}

                </Box>
              ) : (
                <Box>
                  <Card sx={{ p: 3, mb: 3, backgroundColor: "#f9f9f9", border: "1px solid #EBEBEB" }}>
                    <Typography variant="h6" sx={{ textAlign: "center", mb: 2 }}>Enter Card Details</Typography>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Card Number"
                          value={cardDetails.number}
                          onChange={handleCardChange("number")}
                          placeholder="1234 5678 9012 3456"
                          sx={textFieldStyles}
                          inputProps={{ maxLength: 19 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PaymentIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Name on Card"
                          value={cardDetails.name}
                          onChange={handleCardChange("name")}
                          sx={textFieldStyles}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PersonIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Expiry Date"
                          value={cardDetails.expiry}
                          onChange={handleCardChange("expiry")}
                          placeholder="MM/YY"
                          sx={textFieldStyles}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarTodayIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="CVV"
                          value={cardDetails.cvv}
                          onChange={handleCardChange("cvv")}
                          placeholder="123"
                          sx={textFieldStyles}
                          inputProps={{ maxLength: 4 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LockIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Card>


                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Checkbox
                      checked={cardTermsAccepted}
                      onChange={handleCardTermsChange}
                      sx={{ color: "#Ef4444", "&.Mui-checked": { color: "#Ef4444" } }}
                    />
                    <Typography>I accept the Terms & Conditions and Privacy Policy</Typography>
                  </Box>

                  <Card sx={{ p: 2, mb: 3, backgroundColor: "#f9f9f9", border: "1px solid #EBEBEB" }}>
                    <Typography variant="h6" sx={{ textAlign: "center" }}>Total Payment Amount</Typography>
                    <Typography variant="h4" sx={{ textAlign: "center", color: "#Ef4444", fontWeight: "bold" }}>
                      {paymentAmount}
                    </Typography>
                  </Card>

                  <Button
                    variant="contained"
                    fullWidth
                    disabled={!cardTermsAccepted}
                    onClick={handleCardPaymentSubmit}
                    sx={{
                      backgroundColor: "#Ef4444",
                      "&:hover": { backgroundColor: "#D32F2F" },
                      "&:disabled": { backgroundColor: "#cccccc" },
                      height: '48px',
                      fontSize: '1rem'
                    }}
                  >
                    Pay Now Securely
                  </Button>

                  {paymentStatus === "completed" && (
                    <Box sx={{
                      mt: 3,
                      p: 2,
                      backgroundColor: "#e8f5e9",
                      borderRadius: "8px",
                      borderLeft: "4px solid #4caf50"
                    }}>
                      <Typography sx={{ color: "#2e7d32", fontWeight: 'bold' }}>
                        Payment processed successfully!
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Thank you for your purchase. A receipt has been sent to your email.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Card>
          </Grid>
        </Grid>
      </Box>
      <FooterManual />
    </div>
  );
};

export default PaymentInformation;